import React, { useState, useEffect, useRef } from 'react';
import { App, Table, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';

interface MessageSender {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface MessageQuote {
  id: string;
  status: string;
  draft_order?: {
    display_id: string;
  };
}

interface Message {
  id: string;
  text: string;
  item_id: string;
  customer_id: string;
  quote_id: string;
  quote: MessageQuote;
  service_provider_admin?: MessageSender;
  customer?: MessageSender;
}

const { Search } = Input;

const Messages: React.FC = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const loadMessages = async (query = '', offset = 0, limit = 20) => {
    try {
      setLoading(true);
      const params: any = { limit, offset };
      if (query) params.q = query;
      const response = await apiClient.get('/admin/messages', { params });
      setMessages(response.data.messages || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.count || 0,
        current: Math.floor(offset / limit) + 1,
      }));
    } catch (error: any) {
      message.error(error.response?.data?.message || t('common.noData'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
  }, []);

  const handleSearch = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchText(value);
      loadMessages(value, 0, pagination.pageSize);
    }, 400);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    const offset = (page - 1) * pageSize;
    loadMessages(searchText, offset, pageSize);
  };

  const handleRowClick = (record: Message) => {
    navigate(`/quotes/${record.quote_id}`);
  };

  const getSenderName = (record: Message) => {
    if (record.customer_id && record.customer) {
      return `${record.customer.first_name} ${record.customer.last_name}`;
    }
    if (record.service_provider_admin) {
      return `${record.service_provider_admin.first_name} ${record.service_provider_admin.last_name}`;
    }
    return '-';
  };

  const columns = [
    {
      title: t('messages.quoteNumber'),
      key: 'quote_number',
      render: (_: any, record: Message) => record.quote?.draft_order?.display_id || '-',
    },
    {
      title: t('messages.sender'),
      key: 'sender',
      render: (_: any, record: Message) => getSenderName(record),
    },
    {
      title: t('messages.messageText'),
      dataIndex: 'text',
      key: 'text',
      ellipsis: true,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>{t('menu.messages')}</h1>
      </div>

      <Search
        placeholder={t('common.search')}
        onChange={(e) => handleSearch(e.target.value)}
        style={{ marginBottom: 16 }}
        allowClear
      />

      <Table
        columns={columns}
        dataSource={messages}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          ...pagination,
          onChange: handlePageChange,
          showSizeChanger: false,
        }}
      />
    </div>
  );
};

export default Messages;
